/*
 * Loan email extraction (client-side, rule based).
 * Turns a free-text deal email into the structured fields a broker types into
 * a lender portal or a spreadsheet. Each field records where it was found so
 * the UI can show what was read versus what still needs a human.
 *
 * Exported shape: { fields: { key: { value, found } }, missing: [key] }
 */

export const LOAN_FIELDS = [
  { key: "borrower", label: "Borrower", group: "Contact" },
  { key: "contactEmail", label: "Email", group: "Contact" },
  { key: "contactPhone", label: "Phone", group: "Contact" },
  { key: "propertyAddress", label: "Property address", group: "Property" },
  { key: "propertyType", label: "Property type", group: "Property" },
  { key: "units", label: "Units", group: "Property" },
  { key: "occupancy", label: "Occupancy", group: "Property" },
  { key: "purpose", label: "Loan purpose", group: "Loan" },
  { key: "loanAmount", label: "Requested amount", group: "Loan" },
  { key: "value", label: "Value / price", group: "Loan" },
  { key: "ltv", label: "LTV", group: "Loan" },
  { key: "rate", label: "Current rate", group: "Loan" },
  { key: "maturity", label: "Loan maturity", group: "Loan" },
  { key: "noi", label: "NOI", group: "Financials" },
  { key: "dscr", label: "DSCR", group: "Financials" },
  { key: "creditScore", label: "Credit score", group: "Financials" },
  { key: "closeBy", label: "Needs to close by", group: "Timing" },
  { key: "lenderPreference", label: "Lender preference", group: "Timing" },
];

const MONEY = "\\$\\s?[\\d,]+(?:\\.\\d+)?\\s?(?:mm|m|k)?";
const money = (raw) => {
  if (!raw) return "";
  const m = String(raw).replace(/[$,\s]/g, "").toLowerCase();
  const num = parseFloat(m);
  if (!Number.isFinite(num)) return String(raw).trim();
  const mult = m.endsWith("mm") || m.endsWith("m") ? 1_000_000 : m.endsWith("k") ? 1_000 : 1;
  return `$${Math.round(num * mult).toLocaleString("en-US")}`;
};

const first = (text, patterns, transform = (v) => v) => {
  for (const re of patterns) {
    const m = text.match(re);
    if (m && m[1]) return transform(m[1].trim().replace(/[.;,]+$/, ""));
  }
  return "";
};

export const extractLoanFields = (emailText, meta = {}) => {
  const text = String(emailText || "").replace(/\r/g, "");
  const flat = text.replace(/\s+/g, " ");
  const fields = {};

  const NAME = "([A-Z][\\w.&'-]+(?:\\s+[A-Z][\\w.&'-]+){0,4}(?:,?\\s+LLC|,?\\s+Inc\\.?|,?\\s+LP)?)";
  fields.borrower = first(flat, [
    new RegExp(`borrower(?:'s name)?(?: is|:|\\s+name is)?\\s+${NAME}`, "i"),
    new RegExp(`(?:client|owner|sponsor)(?: is|:)\\s*${NAME}`, "i"),
    // Forwarded deal: the original sender is the borrower.
    /Forwarded message[\s-]*From:\s*([A-Z][\w.'-]+(?:\s+[A-Z][\w.'-]+){0,3})\s*</,
  ], (v) => v.split(/\.\s/)[0].replace(/\s+(?:wants?|needs?|is|has|would|owns?|would like)\b.*$/i, "").replace(/[.,]+$/, ""));
  if (!fields.borrower && meta.fromName && !/gtcbrokerage|enterprisebank|oelement/i.test(meta.fromEmail || "")) {
    fields.borrower = meta.fromName;
  }
  fields.contactEmail = first(flat, [/([\w.+-]+@(?!oelement\.ai)[\w-]+\.[\w.-]+)/i]);
  fields.contactPhone = first(flat, [/(\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4})/]);
  fields.propertyAddress = first(flat, [
    /(?:property|address|located at|subject)(?: is|:)?\s*(?:at\s+)?(\d{2,6}\s+[A-Z][\w.'-]*(?:\s+[A-Z][\w.'-]*){0,4}\s+(?:St|Street|Ave|Avenue|Blvd|Boulevard|Rd|Road|Dr|Drive|Way|Ln|Lane|Ct|Court|Pkwy|Hwy)\.?(?:,?\s+(?:Suite|Ste|Unit|#)\s*\w+)?,?\s+[A-Z][\w.'-]+(?:\s+[A-Z][\w.'-]+)?,?\s+[A-Z]{2}(?:\s+\d{5})?)/,
    /(\d{2,6}\s+[A-Z][\w.'-]*(?:\s+[A-Z][\w.'-]*){0,4}\s+(?:St|Street|Ave|Avenue|Blvd|Boulevard|Rd|Road|Dr|Drive|Way|Ln|Lane|Ct|Court|Pkwy|Hwy)\.?(?:,?\s+(?:Suite|Ste|Unit|#)\s*\w+)?,?\s+[A-Z][\w.'-]+(?:\s+[A-Z][\w.'-]+)?,?\s+[A-Z]{2}(?:\s+\d{5})?)/,
    // No street type (e.g. "731 Broadway, Sacramento, CA 95818"): require city, state, zip.
    /(\d{2,6}\s+[A-Z][\w.'-]+(?:\s+[A-Z][\w.'-]+){0,3},\s+[A-Z][\w.'-]+(?:\s+[A-Z][\w.'-]+)?,\s+[A-Z]{2}\s+\d{5})/,
  ]);
  fields.propertyType = first(flat, [
    /(\d+[- ]unit\s+(?:apartment|multifamily|mixed[- ]use)[\w -]*?(?= (?:at|in|on|located|building|property|,|\.)))/i,
    /\b((?:multifamily|multi-family|mixed[- ]use|retail|office|industrial|warehouse|self[- ]storage|hotel|gas station|restaurant|medical office|strip center|duplex|fourplex|triplex|SBA[- ]eligible owner[- ]user)(?:\s+(?:building|property|center))?)\b/i,
  ]);
  fields.units = first(flat, [/(\d{1,4})[- ]unit/i, /units?:\s*(\d{1,4})/i]);
  if (!fields.units) {
    const plex = { duplex: "2", triplex: "3", fourplex: "4", quadplex: "4" };
    const m = flat.match(/\b(duplex|triplex|fourplex|quadplex)\b/i);
    if (m) fields.units = plex[m[1].toLowerCase()];
  }
  fields.occupancy = first(flat, [/(\d{2,3}\s?%)\s*(?:occupied|occupancy|leased)/i, /occupancy(?: is|:)?\s*(\d{2,3}\s?%)/i]);
  fields.purpose = first(flat, [
    /\b(cash[- ]out refinance|rate[- ]and[- ]term refinance|refinance|purchase|acquisition|construction|bridge|SBA 504|SBA 7\(a\)|owner[- ]user purchase)\b/i,
  ], (v) => v.toLowerCase());
  fields.loanAmount = first(flat, [
    new RegExp(`(?:loan (?:amount|request)|looking for|needs?|requesting|borrow|balance)(?: is|:|\\s+of)?\\s*(?:about|around|approximately)?\\s*(${MONEY})`, "i"),
    new RegExp(`(${MONEY})\\s*(?:loan|in financing|of financing)`, "i"),
  ], money);
  fields.value = first(flat, [
    new RegExp(`(?:purchase price|price|valued? (?:at|of)|appraised (?:at|value)|worth)(?: is|:)?\\s*(?:about|around|approximately)?\\s*(${MONEY})`, "i"),
  ], money);
  fields.ltv = first(flat, [/(\d{2}\s?%)\s*LTV/i, /LTV(?: of|:)?\s*(\d{2}\s?%)/i]);
  fields.rate = first(flat, [/(\d{1,2}(?:\.\d{1,3})?\s?%)\s*(?:rate|interest|fixed|variable)/i, /(?:rate|interest)(?: is|:| of)?\s*(\d{1,2}(?:\.\d{1,3})?\s?%)/i]);
  fields.maturity = first(flat, [
    /(?:matur(?:es|ity|ing)|comes? due|balloon(?:s)?|due)(?: date| on| in|:)?\s*((?:January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\.?\s+(?:\d{1,2},?\s+)?\d{4}|\d{1,2}\/\d{1,2}\/\d{2,4}|\d{1,2}\/\d{4})/i,
  ]);
  fields.noi = first(flat, [new RegExp(`NOI[^$\\d]{0,24}?(${MONEY})`, "i")], money);
  fields.dscr = first(flat, [/DSCR(?: is|:| of)?\s*(\d(?:\.\d{1,2})?x?)/i]);
  fields.creditScore = first(flat, [/(?:credit(?: score)?|FICO)(?: is|:| of)?\s*(\d{3})/i]);
  fields.closeBy = first(flat, [
    /(?:close|closing|fund(?:ed|ing)?|need(?:s)? (?:it|this|to close)|done|finished)\s*(?:by|before|no later than|in)\s*((?:January|February|March|April|May|June|July|August|September|October|November|December|Jan|Feb|Mar|Apr|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)\.?\s+\d{1,2}(?:,?\s+\d{4})?|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|\d{1,2}\s+(?:days|weeks))/i,
  ]);
  fields.lenderPreference = first(flat, [
    /(?:prefer(?:s|red)?|looking at|wants?|open to)\s+(?:an?\s+)?((?:SBA|bank|credit union|hard money|private|bridge|agency|CMBS|life company|non-recourse)[\w -]{0,24}?(?:lender|loan|financing|money))/i,
    /(?:prefer(?:s|red)?|open to)\s+(?:an?\s+)?((?:SBA|bank|credit union|hard money|private|agency|CMBS|life company)(?:\s+or\s+(?:bank|credit union|private|SBA))?)/i,
  ]);

  if (!fields.contactEmail && meta.fromEmail) fields.contactEmail = meta.fromEmail;

  const out = {};
  const missing = [];
  LOAN_FIELDS.forEach(({ key }) => {
    const value = String(fields[key] || "").trim();
    out[key] = { value, found: Boolean(value) };
    if (!value) missing.push(key);
  });
  return { fields: out, missing };
};

// Rows for a spreadsheet: header row + one data row per extraction.
export const toSpreadsheetRows = (extractions) => {
  const header = ["Received", "From", "Subject", ...LOAN_FIELDS.map((f) => f.label)];
  const rows = extractions.map((x) => [
    x.receivedAt,
    x.fromEmail,
    x.subject,
    ...LOAN_FIELDS.map((f) => x.fields?.[f.key]?.value || ""),
  ]);
  return [header, ...rows];
};

export const rowsToCsv = (rows) =>
  rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");

export const rowsToTsv = (rows) => rows.map((r) => r.map((c) => String(c ?? "").replace(/\t/g, " ")).join("\t")).join("\n");
